import React, { useState, useEffect, useContext, useRef } from 'react';

import { CONSENT_STATUS, CONSENT_CHANNEL_NAME } from "constants/cookie-consent";
import { getSavedConsent, saveConsent } from "extra/cookie-consent";
import { loadConsentTools, areConsentToolsLoaded, clearConsentToolsData } from "extra/consent-tools";

const CookieConsentContext = React.createContext();
export default CookieConsentContext;


export const CookieConsentContextProvider = props => {

	const [ status, setStatus ] = useState( getSavedConsent());
	const [ is_settings_open, setIsSettingsOpen ] = useState( false );
	const channel_ref = useRef( null );

	// Without consent no data of these tools may stay on the device. This also removes cookies that were set
	// before the banner asked for consent, or written again by a tool while the page reloaded after a withdrawal.
	useEffect(() => {
		status === CONSENT_STATUS.GRANTED ? loadConsentTools() : clearConsentToolsData();
	}, [ status ]);


	// A decision made in another tab has to be applied here as well, otherwise this tab keeps the consent tools running after withdrawal
	useEffect(() => {

		const syncWithSavedConsent = () => {
			const saved = getSavedConsent();

			if ( saved !== CONSENT_STATUS.GRANTED && areConsentToolsLoaded()) {
				clearConsentToolsData();
				return window.location.reload();
			}

			setStatus( saved );
		}

		// Browsers without BroadcastChannel are synchronized when the tab becomes visible again
		const onVisibilityChange = () => document.visibilityState === "visible" && syncWithSavedConsent();

		if ( "BroadcastChannel" in window ) {
			channel_ref.current = new BroadcastChannel( CONSENT_CHANNEL_NAME );
			channel_ref.current.onmessage = syncWithSavedConsent;
		}

		document.addEventListener( "visibilitychange", onVisibilityChange );

		return () => {
			document.removeEventListener( "visibilitychange", onVisibilityChange );
			channel_ref.current && channel_ref.current.close();
			channel_ref.current = null;
		}
	}, []);


	const saveDecision = decision => {
		saveConsent( decision );
		channel_ref.current && channel_ref.current.postMessage( decision );
	}


	const accept = () => {
		saveDecision( CONSENT_STATUS.GRANTED );
		setStatus( CONSENT_STATUS.GRANTED );
		setIsSettingsOpen( false );
	}


	const reject = () => {
		saveDecision( CONSENT_STATUS.DENIED );
		clearConsentToolsData();

		// Scripts that are already running cannot be unloaded, only a reload stops them
		if ( areConsentToolsLoaded()) return window.location.reload();

		setStatus( CONSENT_STATUS.DENIED );
		setIsSettingsOpen( false );
	}


	return (
		<CookieConsentContext.Provider value={{
			status,
			is_settings_open,
			is_banner_visible: status === null || is_settings_open,
			accept,
			reject,
			openSettings: () => setIsSettingsOpen( true )
		}}>
			{ props.children }
		</CookieConsentContext.Provider>
	)
}


export const useCookieConsent = () => useContext( CookieConsentContext );
